'use client';

import classNames from 'classnames';
import { type ButtonHTMLAttributes, forwardRef, type MouseEvent, type ReactNode, type Ref, useMemo } from 'react';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { LinkInfo, Size } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import isAbsoluteLink from '../Link/isAbsoluteLink';
import LinkInfoRenderer from '../LinkInfoRenderer';
import LoadingDots from '../LoadingDots';
import styles from './styles/styles.module.scss';

export type ButtonVariant = 'accent' | 'destructive' | 'ghost' | 'inset-dark' | 'inset-light' | 'inverse' | 'primary' | 'secondary' | 'tertiary';

export type ButtonWidth = 'auto' | 'full' | 'fullOnMobile';

export type ButtonProps = {
  ariaLabel?: string;
  className?: string;
  'data-denali'?: string;
  disabled?: boolean;
  elevated?: boolean;
  icon?: IconDefinition<'orientation'> | ReactNode;
  linkInfo?: LinkInfo;
  loading?: boolean;
  onClick?: (e?: MouseEvent<HTMLElement>) => void;
  preventLocaleHandling?: boolean;
  size?: Size<'sm' | 'md' | 'lg'>;
  text: ReactNode;
  title?: string;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  variant?: ButtonVariant;
  width?: ButtonWidth;
  allowWrap?: boolean;
  stopPropagation?: boolean;
};

const Button = (
  {
    ariaLabel,
    className,
    'data-denali': dataDenali,
    disabled,
    elevated = false,
    icon,
    linkInfo,
    loading,
    onClick,
    preventLocaleHandling = false,
    size = 'md',
    text,
    title,
    type = 'button',
    variant = 'secondary',
    width = 'auto',
    allowWrap = false,
    stopPropagation = false
  }: ButtonProps,
  ref: Ref<HTMLElement>
) => {
  const languageRegionCode = useLanguageRegionCode();

  const onButtonClick = (e: MouseEvent<HTMLElement>) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    if (onClick) {
      onClick(e);
    }
  };
  const content = useMemo(() => {
    return icon ? (
      <>
        {typeof icon === 'object' && 'Component' in icon ? (
          <IconRenderer icon={icon} defaults={{ color: 'currentColor', size: size === 'sm' ? 'sm' : 'md' }} />
        ) : (
          icon
        )}
        {text ? <span>{text}</span> : null}
      </>
    ) : (
      <>
        <span>{text}</span>
      </>
    );
  }, [icon, size, text]);

  const variantClass = styles[variant === 'inset-dark' ? 'insetDark' : variant === 'inset-light' ? 'insetLight' : variant];

  const classes = classNames(
    className,
    styles.button,
    styles[size],
    variantClass,
    styles[width],
    elevated && styles.elevated,
    icon && text ? styles.lessPaddingLeft : undefined, // When just an icon is provided, by the ToggleButton for example, don't adjust the default padding
    disabled && styles.disabled, // An anchor tag cannot be disabled, so we apply a disabled class instead
    loading && styles.loading,
    allowWrap && styles.allowWrap,
    linkInfo && !disabled && styles.link
  );
  if (linkInfo) {
    if (disabled) {
      return (
        <span {...setDenaliAttribute(dataDenali || 'Button')} className={classes} ref={ref} title={title}>
          {content}
        </span>
      );
    }

    const isAbsolute = isAbsoluteLink(linkInfo.href);

    return (
      <LinkInfoRenderer
        ariaLabel={ariaLabel}
        className={classes}
        {...setDenaliAttribute(dataDenali || 'Button')}
        linkInfo={{ ...linkInfo, href: isAbsolute || preventLocaleHandling ? linkInfo.href : wrapUrlSafe(linkInfo.href, languageRegionCode) }}
        onClick={loading ? undefined : onButtonClick}
        title={title}
        ref={ref as React.Ref<HTMLAnchorElement>}
      >
        {content}
        {loading && !disabled ? <LoadingDots /> : null}
      </LinkInfoRenderer>
    );
  }
  return (
    // Need to disable the linting rule because it wants the button type to be hardcoded https://github.com/jsx-eslint/eslint-plugin-react/issues/1555
    <button
      {...setDenaliAttribute(dataDenali || 'Button')}
      aria-label={ariaLabel}
      aria-busy={loading && !disabled ? true : undefined}
      className={classes}
      onClick={loading ? undefined : onButtonClick}
      disabled={disabled}
      title={title}
      type={type}
      ref={ref as Ref<HTMLButtonElement>}
    >
      {content}
      {loading && !disabled ? <LoadingDots className={styles.loadingDots} /> : null}
    </button>
  );
};

export default forwardRef(Button);
